import { motion } from "framer-motion";
import { Cog, Activity, Flame, Wrench, ArrowUpRight } from "lucide-react";
import { SERVICES } from "../data";

const ICONS = { Cog, Activity, Flame, Wrench };

const Card = ({ s, i, wide }) => {
  const Icon = ICONS[s.icon];
  return (
    <motion.div
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`service-card-${s.id}`}
      className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-[#080808] transition-colors duration-500 hover:border-[#ffb000]/60 ${
        wide ? "md:col-span-2" : ""
      }`}
    >
      {/* image */}
      <div className="absolute inset-0">
        <img
          src={s.img}
          alt={s.title}
          className="h-full w-full object-cover opacity-25 transition-all duration-700 group-hover:scale-110 group-hover:opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/40" />
      </div>

      {/* glow ring on hover */}
      <div className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 shadow-[inset_0_0_60px_rgba(255,176,0,0.15)] transition-opacity duration-500 group-hover:opacity-100" />

      <div className="relative flex min-h-[340px] flex-col justify-between p-8 md:min-h-[380px] md:p-10">
        <div className="flex items-start justify-between">
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/15 bg-white/5 backdrop-blur-md transition-colors duration-500 group-hover:border-[#ffb000]/50">
            <Icon className="h-6 w-6 text-[#ffb000]" strokeWidth={1.8} />
          </div>
          <span className="font-display text-5xl outline-text md:text-6xl">{s.n}</span>
        </div>

        <div>
          <h3 className="font-accent text-2xl font-600 uppercase tracking-wide text-white md:text-3xl">
            {s.title}
          </h3>
          <p className="mt-4 max-w-md font-body text-sm leading-relaxed text-neutral-400">{s.copy}</p>
          <div className="mt-6 flex items-center gap-2 font-accent text-xs uppercase tracking-[0.25em] text-[#ffb000] opacity-0 transition-opacity duration-500 group-hover:opacity-100">
            Enquire <ArrowUpRight className="h-4 w-4" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section id="arsenal" data-testid="services" className="relative px-5 py-28 md:px-10 md:py-40">
      <div className="mx-auto max-w-[1400px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 flex flex-col gap-4 md:mb-20 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <span className="font-accent text-xs uppercase tracking-[0.4em] text-[#ffb000]">The Arsenal</span>
            <h2 className="mt-4 font-display text-4xl font-700 uppercase leading-none chrome-text md:text-6xl">
              Core Services
            </h2>
          </div>
          <p className="max-w-sm font-body text-sm leading-relaxed text-neutral-400">
            Workshop-grade capability across the full mechanical spectrum. If it drives, we can diagnose it,
            rebuild it, and hand it back better than new.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {SERVICES.map((s, i) => (
            <Card key={s.id} s={s} i={i} wide={i === 0 || i === 3} />
          ))}
        </div>
      </div>
    </section>
  );
};
